import { supabase } from '../supabaseClient';
import { Sentry } from './sentry';
import { getLastRecoveryAction } from './appRecovery';
import { getSupabaseCircuitState } from './supabaseCircuitBreaker';

const QUEUE_KEY = 'ss_client_diagnostics_queue_v1';
const LAST_SNAPSHOT_KEY = 'ss_client_diagnostics_last_snapshot_v1';
const DEDUPE_MS = 30 * 1000;
const MAX_QUEUE = 25;
const MAX_STRING = 500;
const recentEvents = new Map();

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function sessionStorageSafe() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function safeParse(raw, fallback = null) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function truncate(value, max = MAX_STRING) {
  if (value === null || value === undefined) return null;
  const text = String(value);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function sanitizeValue(value, depth = 0) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return truncate(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return {
      name: truncate(value.name, 120),
      message: truncate(value.message),
    };
  }
  if (depth >= 3) return truncate(JSON.stringify(value));
  if (Array.isArray(value)) return value.slice(0, 20).map(item => sanitizeValue(item, depth + 1));
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !/token|secret|password|authorization|apikey|api_key/i.test(key))
        .slice(0, 40)
        .map(([key, item]) => [key, sanitizeValue(item, depth + 1)])
    );
  }
  return truncate(value);
}

function sanitizeError(error) {
  if (!error) return null;
  return {
    name: truncate(error.name || 'Error', 120),
    message: truncate(error.message || error),
  };
}

function diagnosticSendFailureError(error) {
  const message = truncate(error?.message || error || 'Client diagnostic send failed');
  const wrapped = new Error(`Client diagnostic send failed: ${message}`);
  wrapped.name = 'ClientDiagnosticSendFailure';
  if (error instanceof Error && error.stack) {
    wrapped.stack = error.stack;
  }
  return wrapped;
}

function getSessionKey() {
  return sessionStorageSafe()?.getItem('ss_session_key') || null;
}

function dateFromCacheValue(value) {
  if (!value) return null;
  if (typeof value === 'number') return new Date(value).toISOString();
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  return null;
}

function summarizeLocalCache() {
  const ls = storage();
  if (!ls) return {};
  const prefixes = {
    dashboard: 'ss_dashboard_apps_cache_v1:',
    pipeline: 'ss_pipeline_stats_cache_v1:',
    pro_detail: 'ss_pro_detail_cache_v4:',
    subscription: 'ss_subscription_cache_v1:',
  };

  const summary = {};
  for (const [name, prefix] of Object.entries(prefixes)) {
    const keys = Object.keys(ls).filter(key => key.startsWith(prefix));
    const dates = keys
      .map(key => safeParse(ls.getItem(key), {}))
      .map(entry => dateFromCacheValue(entry?.cached_at))
      .filter(Boolean)
      .sort();
    summary[name] = {
      count: keys.length,
      oldest_cached_at: dates[0] || null,
      newest_cached_at: dates[dates.length - 1] || null,
    };
  }
  return summary;
}

async function getServiceWorkerVersion() {
  try {
    if (typeof caches !== 'undefined') {
      const names = await caches.keys();
      const versions = [...new Set(
        names
          .map(name => name.match(/sitescope-\d+/)?.[0])
          .filter(Boolean)
      )].sort().reverse();
      if (versions.length > 0) return versions.slice(0, 3).join(',');
    }
  } catch {
    // Cache inspection is diagnostic-only.
  }

  try {
    return navigator.serviceWorker?.controller?.scriptURL || null;
  } catch {
    return null;
  }
}

async function buildPayload({
  user,
  surface,
  page,
  da,
  message,
  metadata,
  timing,
  error,
} = {}) {
  return {
    session_key: getSessionKey(),
    surface: truncate(surface, 80),
    page: truncate(page, 80),
    da_id: da?.id || null,
    da_number: truncate(da?.da_number || da?.council_application_number || da?.planning_portal_app_number, 120),
    council: truncate(da?.council, 160),
    message: truncate(message || error?.message || null),
    url_path: typeof window !== 'undefined'
      ? truncate(`${window.location.pathname}${window.location.search}`, 300)
      : null,
    app_version: process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || process.env.REACT_APP_GIT_SHA || null,
    service_worker_version: await getServiceWorkerVersion(),
    user_agent: typeof navigator !== 'undefined' ? truncate(navigator.userAgent) : null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    viewport: typeof window !== 'undefined' ? {
      width: window.innerWidth,
      height: window.innerHeight,
      device_pixel_ratio: window.devicePixelRatio || 1,
      visibility: document.visibilityState,
    } : {},
    circuit: getSupabaseCircuitState(),
    cache: summarizeLocalCache(),
    auth: {
      signed_in: Boolean(user?.id),
      email_present: Boolean(user?.email),
    },
    timing: sanitizeValue(timing || {}),
    metadata: sanitizeValue({
      ...(metadata || {}),
      recovery: getLastRecoveryAction(),
      error: sanitizeError(error),
    }),
  };
}

function normaliseDiagnosticMessage(message) {
  return String(message || '')
    .replace(/Retrying in \d+s\./i, 'Retrying soon.')
    .replace(/\b\d+s\b/g, 'Xs');
}

function dedupeKey(eventType, payload) {
  return [
    eventType,
    payload.da_id || '',
    payload.surface || '',
    normaliseDiagnosticMessage(payload.message),
    normaliseDiagnosticMessage(payload.metadata?.error?.message),
  ].join('|');
}

function shouldDedupe(eventType, payload) {
  const key = dedupeKey(eventType, payload);
  const now = Date.now();
  const last = recentEvents.get(key) || 0;
  if (now - last < DEDUPE_MS) return true;
  recentEvents.set(key, now);
  return false;
}

function readQueue() {
  return safeParse(storage()?.getItem(QUEUE_KEY), []) || [];
}

function writeQueue(events) {
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(QUEUE_KEY, JSON.stringify(events.slice(-MAX_QUEUE)));
  } catch {
    // Best-effort only.
  }
}

function queueEvent(event) {
  const queued = [...readQueue(), event].slice(-MAX_QUEUE);
  writeQueue(queued);
  return queued.length;
}

function writeLastSnapshot(event, delivery = {}) {
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(LAST_SNAPSHOT_KEY, JSON.stringify({
      captured_at: new Date().toISOString(),
      delivery,
      event,
    }));
  } catch {
    // Best-effort only.
  }
}

function captureDiagnosticSendFailure(error, event) {
  Sentry.captureException(diagnosticSendFailureError(error), {
    fingerprint: [
      'client-diagnostic-send-failure',
      event?.event_type || 'unknown',
      error?.code || error?.name || 'unknown',
    ],
    tags: {
      sitescope_area: 'client_diagnostics',
      sitescope_event_type: event?.event_type || 'unknown',
      sitescope_severity: event?.severity || 'unknown',
      sitescope_surface: event?.payload?.surface || 'unknown',
      sitescope_send_error_code: error?.code || 'unknown',
    },
    extra: {
      page: event?.payload?.page || null,
      message: event?.payload?.message || null,
      online: event?.payload?.online ?? null,
      circuit: event?.payload?.circuit || null,
      send_error: sanitizeValue(error),
    },
  });
}

async function sendEvent({ event_type, severity, payload }) {
  const { error } = await supabase.rpc('record_client_diagnostic_event', {
    p_event_type: event_type,
    p_severity: severity,
    p_payload: payload,
  });
  if (error) throw error;
  return true;
}

export async function flushQueuedDiagnosticEvents() {
  const queued = readQueue();
  if (!queued.length || (typeof navigator !== 'undefined' && !navigator.onLine)) return 0;

  const remaining = [];
  let sent = 0;
  for (const event of queued) {
    try {
      await sendEvent(event);
      sent += 1;
    } catch {
      remaining.push(event);
    }
  }
  writeQueue(remaining);
  return sent;
}

export function getQueuedDiagnosticEventCount() {
  return readQueue().length;
}

export function getLastDiagnosticsSnapshot() {
  return safeParse(storage()?.getItem(LAST_SNAPSHOT_KEY), null);
}

async function createDiagnosticEvent(eventType, severity, options = {}) {
  const payload = await buildPayload(options);
  return { event_type: eventType, severity, payload };
}

export async function reportClientDiagnostic(eventType, options = {}) {
  if (!eventType || typeof supabase.rpc !== 'function') return false;
  const severity = ['info', 'warning', 'error', 'critical'].includes(options.severity)
    ? options.severity
    : 'warning';
  const payload = await buildPayload(options);
  if (shouldDedupe(eventType, payload)) return false;

  const event = { event_type: eventType, severity, payload };
  try {
    await flushQueuedDiagnosticEvents();
    await sendEvent(event);
    return true;
  } catch (err) {
    queueEvent(event);
    captureDiagnosticSendFailure(err, event);
    console.warn('[SiteScope] Client diagnostic queued:', err?.message || err);
    return false;
  }
}

export async function fetchClientDiagnostics(limit = 80) {
  const { data, error } = await supabase.rpc('get_client_diagnostics_events', {
    p_limit: limit,
  });
  if (error) throw error;
  return data?.events || [];
}

export async function sendCurrentDiagnosticsSnapshot(user, metadata = {}) {
  if (typeof supabase.rpc !== 'function') {
    return {
      sent: false,
      queued: false,
      status: 'unavailable',
      message: 'Diagnostics are unavailable in this app session.',
      queue_count: getQueuedDiagnosticEventCount(),
    };
  }

  const circuitAtCapture = getSupabaseCircuitState();
  const event = await createDiagnosticEvent('manual_phone_snapshot', 'info', {
    severity: 'info',
    user,
    surface: 'admin_diagnostics',
    page: 'settings',
    message: 'Manual phone diagnostics snapshot',
    metadata: {
      ...metadata,
      delivery: {
        manual: true,
        circuit_open_at_capture: circuitAtCapture.open,
      },
    },
  });

  writeLastSnapshot(event, {
    status: 'captured',
    circuit_open_at_capture: circuitAtCapture.open,
    queue_count: getQueuedDiagnosticEventCount(),
  });

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    const queueCount = queueEvent(event);
    const result = {
      sent: false,
      queued: true,
      status: 'queued_offline',
      message: `Snapshot saved locally and queued because this device is offline. Queue: ${queueCount}.`,
      queue_count: queueCount,
      circuit_open_at_capture: circuitAtCapture.open,
    };
    writeLastSnapshot(event, result);
    return result;
  }

  try {
    const flushed = await flushQueuedDiagnosticEvents();
    await sendEvent(event);
    const result = {
      sent: true,
      queued: false,
      status: circuitAtCapture.open ? 'sent_circuit_bypassed' : 'sent',
      message: circuitAtCapture.open
        ? `Snapshot sent directly while the local Supabase circuit was open. Flushed ${flushed} queued event(s).`
        : (flushed > 0 ? `Snapshot sent. Flushed ${flushed} queued event(s).` : 'Snapshot sent.'),
      queue_count: getQueuedDiagnosticEventCount(),
      flushed_count: flushed,
      circuit_open_at_capture: circuitAtCapture.open,
    };
    writeLastSnapshot(event, result);
    return result;
  } catch (err) {
    const queueCount = queueEvent(event);
    captureDiagnosticSendFailure(err, event);
    const result = {
      sent: false,
      queued: true,
      status: circuitAtCapture.open ? 'queued_circuit_open_send_failed' : 'queued_send_failed',
      message: `Snapshot saved locally and queued. Send failed: ${err?.message || err}. Queue: ${queueCount}.`,
      queue_count: queueCount,
      circuit_open_at_capture: circuitAtCapture.open,
      error: sanitizeError(err),
    };
    writeLastSnapshot(event, result);
    console.warn('[SiteScope] Diagnostics snapshot queued:', err?.message || err);
    return result;
  }
}

export function installClientDiagnostics(user) {
  const onError = event => {
    reportClientDiagnostic('client_js_error', {
      severity: 'error',
      user,
      surface: 'global',
      page: 'app',
      message: event.message || 'Unhandled JavaScript error',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      error: event.error || new Error(event.message || 'Unhandled JavaScript error'),
    });
  };

  const onUnhandledRejection = event => {
    reportClientDiagnostic('client_unhandled_rejection', {
      severity: 'error',
      user,
      surface: 'global',
      page: 'app',
      message: event.reason?.message || 'Unhandled promise rejection',
      error: event.reason instanceof Error ? event.reason : new Error(String(event.reason || 'Unhandled promise rejection')),
    });
  };

  const onOnline = () => flushQueuedDiagnosticEvents();

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  window.addEventListener('online', onOnline);
  flushQueuedDiagnosticEvents();

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    window.removeEventListener('online', onOnline);
  };
}
